import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import TermsAndConditionsDialog from "./TermsAndConditionsDialog";
import RegisterFields from "./RegisterFields";
import { authService } from "@/services/auth.service";
import { toast } from "sonner";
import { PasswordInput } from "@/components/ui/password-input";

interface LoginFormProps {
  isLogin: boolean;
  selectedRole: "client" | "therapist" | null;
  onSubmit: (formData: any) => Promise<void>;
  onToggleAuthMode: () => void;
  loading: boolean;
  errorMessage: string | null;
  directLogin?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({
  isLogin,
  selectedRole,
  onSubmit,
  onToggleAuthMode,
  loading,
  errorMessage,
  directLogin = false,
}) => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = React.useState(false);
  const [isResettingPassword, setIsResettingPassword] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For registration, validate terms are accepted via checkbox
    if (!isLogin && !termsAccepted) {
      // Show terms dialog if not accepted yet
      setTermsDialogOpen(true);
      return;
    }

    const formData = {
      email,
      password,
      firstName: isLogin ? undefined : firstName,
      lastName: isLogin ? undefined : lastName,
      company: isLogin ? undefined : company,
      termsAccepted: isLogin ? undefined : termsAccepted,
    };

    await onSubmit(formData);
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setTermsDialogOpen(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsResettingPassword(true);
    try {
      await authService.resetPassword(email);
      toast.success("Password reset instructions have been sent to your email. Please check your inbox.");
    } catch (error) {
      console.error("Password reset error:", error);
      if (error instanceof Error) {
        if (error.message.includes("temporarily unavailable")) {
          toast.error("Password reset service is temporarily unavailable. Please try again later or contact support.");
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error("Failed to send reset instructions. Please try again later.");
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Get the appropriate button text based on role and login state
  const getButtonText = () => {
    if (loading) {
      return isLogin ? "Signing in..." : "Creating account...";
    }
    
    if (isLogin) {
      return selectedRole === "therapist" ? "Sign In as Therapist" : "Sign In";
    }
    
    if (selectedRole === "therapist") {
      return "Create Therapist Account";
    }
    
    return "Create Account";
  };

  const getRoleLabel = () => {
    if (!selectedRole) return "";
    return selectedRole === "therapist" ? "Therapist" : "Journaler";
  };

  return (
    <>
      <div className="flex flex-col items-center mb-6">
        <img src="/clou_logo.png" alt="Clou Logo" style={{ height: 56 }} />
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {directLogin && selectedRole && (
          <div className="mb-4 text-center">
            <p className="text-[#333B44] font-medium">
              {isLogin ? `${selectedRole === 'client' ? 'Journaler Login' : getRoleLabel() + ' Login'}` : `Register as ${getRoleLabel()}`}
            </p>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {isLogin && (
            <Button
              type="button"
              variant="link"
              className="text-sm text-[#3BAFDA] hover:text-[#1A6690] p-0 h-auto"
              onClick={handleForgotPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? "Sending..." : "Forgot password?"}
            </Button>
          )}
        </div>

        {!isLogin && (
          <RegisterFields 
            firstName={firstName}
            lastName={lastName}
            company={company}
            termsAccepted={termsAccepted}
            selectedRole={selectedRole}
            onFirstNameChange={(value) => setFirstName(value)}
            onLastNameChange={(value) => setLastName(value)}
            onCompanyChange={(value) => setCompany(value)}
            onTermsAcceptedChange={(value) => setTermsAccepted(value)}
            onOpenTermsDialog={() => setTermsDialogOpen(true)}
          />
        )}

        {errorMessage && (
          <p className="text-red-500 text-sm">{errorMessage}</p>
        )}

        <Button 
          type="submit" 
          className="w-full" 
          disabled={loading || (!isLogin && !termsAccepted)}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isLogin ? "Signing in..." : "Creating account..."}
            </>
          ) : (
            <>{getButtonText()}</>
          )}
        </Button>

        <div className="text-center">
          <Button
            type="button"
            variant="link"
            className="text-[#3BAFDA] hover:text-[#1A6690]"
            onClick={onToggleAuthMode}
          >
            {isLogin 
              ? `Need an account? Sign up${selectedRole === 'therapist' ? ' as a Therapist.' : selectedRole ? ' as a Journaler - it\'s free!' : ''}` 
              : `Already have an account? Log in${selectedRole === 'client' ? ' as Journaler' : selectedRole ? ` as ${selectedRole}` : ''}`}
          </Button>
        </div>

        <TermsAndConditionsDialog
          open={termsDialogOpen}
          onOpenChange={setTermsDialogOpen}
          onAccept={handleAcceptTerms}
          userType={selectedRole || "client"}
          isSubmitting={loading}
        />
      </form>
    </>
  );
};

export default LoginForm;
